export const AUDIENCE_VOICES = ["business", "technical", "caseStudy"] as const;

export type AudienceVoice = (typeof AUDIENCE_VOICES)[number];

export const AUDIENCE_LABELS: Record<AudienceVoice, string> = {
  business: "Business owner",
  technical: "Technical proof",
  caseStudy: "Case study",
};

export const AUDIENCE_HINTS: Record<AudienceVoice, string> = {
  business: "Plain language about messy day-to-day work and practical benefit.",
  technical: "Proof language: systems, integrations, uptime, and how it is built.",
  caseStudy: "What changed for a real client: situation, work, result.",
};

export const AUDIENCE_STORAGE_KEY = "wpcms-audience-voice";

export type AudienceCopy = {
  heading?: string;
  subheading?: string;
  intro?: string;
  body?: string;
  buttonText?: string;
  items?: unknown[];
};

export type AudienceVariants = Partial<Record<AudienceVoice, AudienceCopy>>;

const COPY_KEYS = ["heading", "subheading", "intro", "body", "buttonText"] as const;

export function isAudienceVoice(value: unknown): value is AudienceVoice {
  return AUDIENCE_VOICES.includes(value as AudienceVoice);
}

export function parseAudienceVoice(value: unknown, fallback: AudienceVoice = "business"): AudienceVoice {
  return isAudienceVoice(value) ? value : fallback;
}

function asCopy(value: unknown): AudienceCopy | undefined {
  if (!value || typeof value !== "object") return undefined;
  return value as AudienceCopy;
}

export function getAudienceVariants(content: Record<string, unknown>): AudienceVariants {
  const raw = content.audienceVariants;
  if (!raw || typeof raw !== "object") return {};
  const source = raw as Record<string, unknown>;
  const variants: AudienceVariants = {};
  for (const voice of AUDIENCE_VOICES) {
    const copy = asCopy(source[voice]);
    if (copy) variants[voice] = copy;
  }
  return variants;
}

export function copyHasText(copy: AudienceCopy | undefined): boolean {
  if (!copy) return false;
  return COPY_KEYS.some((key) => String(copy[key] ?? "").trim().length > 0) || Array.isArray(copy.items);
}

export function availableAudienceVoices(content: Record<string, unknown>): AudienceVoice[] {
  const variants = getAudienceVariants(content);
  const voices = AUDIENCE_VOICES.filter((voice) => copyHasText(variants[voice]));
  if (voices.length === 0) {
    return content.audienceEnabled ? ["business"] : [];
  }
  const fallback = parseAudienceVoice(content.defaultAudience);
  if (!voices.includes(fallback) && copyHasText(baseCopy(content))) {
    return [fallback, ...voices.filter((voice) => voice !== fallback)];
  }
  return voices;
}

function baseCopy(content: Record<string, unknown>): AudienceCopy {
  return {
    heading: content.heading ? String(content.heading) : undefined,
    subheading: content.subheading ? String(content.subheading) : undefined,
    intro: content.intro ? String(content.intro) : undefined,
    body: content.body ? String(content.body) : undefined,
    buttonText: content.buttonText ? String(content.buttonText) : undefined,
    items: Array.isArray(content.items) ? content.items : undefined,
  };
}

export function resolveAudienceCopy(
  content: Record<string, unknown>,
  voice: AudienceVoice,
): Record<string, unknown> {
  const variants = getAudienceVariants(content);
  const variant = variants[voice];
  if (!variant) return content;
  return {
    ...content,
    heading: variant.heading ?? content.heading,
    subheading: variant.subheading ?? content.subheading,
    intro: variant.intro ?? content.intro,
    body: variant.body ?? content.body,
    buttonText: variant.buttonText ?? content.buttonText,
    items: variant.items ?? content.items,
  };
}

export function pageAllowsAudienceSwitch(blocks: Array<{ content: Record<string, unknown> }>): boolean {
  return blocks.some((block) => {
    if (!block.content.allowVisitorSwitch) return false;
    return availableAudienceVoices(block.content).length > 1;
  });
}
